import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const PLANS = [
  {
    pill: 'Starter',
    title: 'Launch Plan',
    price: '$ X,XXX',
    desc: 'For businesses that need a sharp web presence and a strong first impression.',
    features: [
      'Single-page premium website',
      'Mobile responsive layout',
      'Basic SEO setup',
      'Contact + enquiry integration',
      '2 revision rounds',
      'Launch support',
    ],
  },
  {
    pill: 'Most Popular',
    title: 'Growth Plan',
    price: '$ X,XXX',
    desc: 'For teams ready to combine design, marketing, and automation in one system.',
    features: [
      'Multi-section business website',
      'On-page & off-page SEO',
      'Google Business profile help',
      'Social media support',
      'AI chatbot / automation setup',
      'Monthly performance review',
    ],
  },
  {
    pill: 'Advanced',
    title: 'Scale Plan',
    price: '$ X,XXX',
    desc: 'For brands that want product workflows, lead systems, and higher-touch growth support.',
    features: [
      'Custom product or dashboard UI',
      'Advanced automation workflows',
      'CRM / WhatsApp integrations',
      'Dedicated account guidance',
      'Conversion optimization',
      'Priority support',
    ],
  },
];

const VALUE_PROPS = [
  { title: 'Launch in 14 Days', desc: 'Your first polished experience ships quickly without sacrificing quality.' },
  { title: 'No Lock-in Contracts', desc: 'Month-to-month flexibility with clear deliverables and momentum.' },
  { title: 'Monthly ROI Reports', desc: 'Traffic, leads, campaign signals, and action items in one place.' },
];

export default function SubscriptionPlan({ onOpenEnquiry }) {
  const ref = useRef(null);

  useEffect(() => {
    gsap.from(ref.current.querySelector('.plan-head'), {
      opacity: 0, y: 30, duration: 0.7, ease: 'power3.out',
      scrollTrigger: { trigger: ref.current, start: 'top 78%' },
    });
    gsap.from(ref.current.querySelectorAll('.plan-card'), {
      opacity: 0, y: 42, stagger: 0.1, duration: 0.75, ease: 'power3.out',
      scrollTrigger: { trigger: ref.current, start: 'top 72%' },
    });
    gsap.from(ref.current.querySelectorAll('.vp-card'), {
      opacity: 0, y: 28, stagger: 0.08, duration: 0.55, ease: 'power3.out',
      scrollTrigger: { trigger: ref.current, start: 'top 68%' },
    });
  }, []);

  return (
    <section id="plans" ref={ref} className="section section-alt">
      <div className="container">
        <div className="section-heading plan-head">
          <p className="eyebrow">Subscription Plans</p>
          <h2 className="heading">
            Choose the plan that fits
            <br />
            <span className="gradient-text">your next stage of growth.</span>
          </h2>
          <p className="subtitle">
            Three clear options, each designed to move from launch to long-term
            growth without scattered vendors or unclear ownership.
          </p>
        </div>

        <div className="plan-grid">
          {PLANS.map((plan) => (
            <div key={plan.title} className="plan-card">
              <div className="plan-header">
                <div>
                  <div className="plan-pill">{plan.pill}</div>
                  <div className="plan-title">{plan.title}</div>
                  <div className="plan-desc">{plan.desc}</div>
                </div>
                <div className="plan-price">
                  <div className="plan-label">Starting at</div>
                  {plan.price}
                  <div className="plan-period">/ month</div>
                </div>
              </div>

              <div className="plan-feature-list">
                {plan.features.map((feature) => (
                  <div key={feature} className="feature-item">
                    <span>✓</span>
                    <div>{feature}</div>
                  </div>
                ))}
              </div>

              <button className="btn btn-primary plan-cta" type="button" onClick={onOpenEnquiry}>
                Start Consultation
              </button>
            </div>
          ))}
        </div>

        <div className="value-props value-props-row">
          {VALUE_PROPS.map((v) => (
            <div key={v.title} className="value-card vp-card">
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div className="services-icon">{v.icon}</div>
                <div>
                  <h4>{v.title}</h4>
                  <p>{v.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
